import { useEffect, useMemo } from "react";

import { useMeditationCustomVoice } from "~/features/meditations/hooks/use-meditation-custom-voice";
import type { RequestableVoiceEntry } from "~/features/meditations/hooks/use-meditation-custom-voice";
import {
    resolveMeditationVoiceOptions,
    type MeditationVoiceContext,
    type MeditationVoiceOption,
} from "~/features/meditations/hooks/meditation-voice-options";
import type { SelectableVoice } from "~/features/meditations/types";
import { getAvailableMeditationVoices } from "~/features/meditations/types";
import { useHwaAccess } from "~/features/hwa-access/hooks/use-hwa-access";
import { useFeatureFlag } from "~/hooks/use-account-feature-flags";

export type {
    MeditationVoiceContext,
    MeditationVoiceOption,
} from "~/features/meditations/hooks/meditation-voice-options";

export interface MeditationVoiceOptions {
    options: MeditationVoiceOption[];
    // AIDEV-NOTE: Default selection — the user's persisted last pick FOR THIS
    // CONTEXT when still a valid option, else the context's account default:
    // general = owned custom voice → Brad; dpmp = Michelle → owned custom voice
    // → Brad. Inherited practitioner voices are choices, never implicit defaults.
    defaultVoice: SelectableVoice;
    customVoiceAvailable: boolean;
    // AIDEV-NOTE: Stable identity of the custom-voice SET. Picker surfaces key their
    // re-default effect on this, not on customVoiceAvailable — with a list, that
    // boolean stays true while the set changes (a second practitioner's clone lands),
    // so the picker would never re-default.
    customVoiceKey: string;
    /** Active practitioners with no voice yet — the promo audience. */
    requestableVoices: RequestableVoiceEntry[];
}

// AIDEV-NOTE: Which set of INHERITED practitioner voices the client has already been
// defaulted onto. Deliberately not keyed by context and deliberately ignores the
// client's OWN clone: this exists so that when a practitioner first generates and
// accepts a voice, their clients switch to it once. A practitioner's own voice follows
// the ordinary precedence in resolveMeditationVoiceOptions and never uses this.
const INHERITED_VOICE_SEEN_KEY = "meditation-voice-inherited-seen";

function readSeenInheritedKey(): string | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem(INHERITED_VOICE_SEEN_KEY);
    } catch {
        return null;
    }
}

function rememberSeenInheritedKey(key: string) {
    try {
        window.localStorage.setItem(INHERITED_VOICE_SEEN_KEY, key);
    } catch {
        // Storage unavailable — the switch simply repeats next visit.
    }
}

// AIDEV-NOTE: Last user-chosen voice per context, persisted so pickers stop
// resetting to the account default on every visit / once the custom-voice probe
// resolves. Each picker's onChange calls rememberMeditationVoice(voice, context);
// defaultVoice below prefers the stored value while it's still a valid option.
// Explicit ?voice= URL overrides are applied by callers and are NOT persisted.
const VOICE_PREFERENCE_KEYS: Record<MeditationVoiceContext, string> = {
    general: "meditation-voice-preference",
    dpmp: "meditation-voice-preference-dpmp",
};

export function rememberMeditationVoice(
    voice: SelectableVoice,
    context: MeditationVoiceContext = "general",
) {
    try {
        window.localStorage.setItem(VOICE_PREFERENCE_KEYS[context], voice);
    } catch {
        // Storage unavailable (private mode / SSR) — the preference just won't stick.
    }
}

function readVoicePreference(context: MeditationVoiceContext): string | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem(VOICE_PREFERENCE_KEYS[context]);
    } catch {
        return null;
    }
}

// AIDEV-NOTE: Single source of truth for meditation voice picker options across
// all surfaces (session meditations, resources exercise player, part check-ins).
export function useMeditationVoiceOptions({
    enabled = true,
    context = "general",
}: {
    enabled?: boolean;
    context?: MeditationVoiceContext;
} = {}): MeditationVoiceOptions {
    const { data: customVoice } = useMeditationCustomVoice({ enabled });
    // AIDEV-NOTE: Tolerate the legacy single-voice shape while an older bundle or a
    // cached response is still in flight mid-deploy.
    const customVoices = useMemo(() => {
        if (customVoice?.voices) return customVoice.voices;
        if (customVoice?.available) {
            return [
                {
                    value: customVoice.value,
                    label: customVoice.label ?? "Your voice",
                    shortLabel: "You",
                    ownedBySelf: true,
                    practitionerId: null,
                },
            ];
        }
        return [];
    }, [customVoice]);
    const available = customVoices.length > 0;

    // AIDEV-NOTE: DPMP-gated catalog voices (e.g. Michelle Glass) only appear for
    // accounts that enabled the DPMP® package (`enable-dpmp`, user opt-in; formerly
    // gated on `show-resources-navigation`).
    const includeResourcesVoices = useFeatureFlag("enable-dpmp");
    // AIDEV-NOTE: Tamala Floyd's voice follows the HWA PURCHASE, not the release flag. It
    // rode `hwa-launch` alone until 2026-10-27, which meant anyone in the launch segment
    // (or everyone, once the flag goes global) could pick the voice of a course they had
    // never bought. `useHwaAccess` checks the flag itself and returns nothing unlocked
    // when it is off, so this one call covers both questions and fails closed while the
    // entitlement is still resolving.
    //
    // AIDEV-NOTE: The server does not trust this. `resolveEntitledVoice` re-checks on
    // every generation path (hwa-voice-gate.server.ts) — her voice may yet be pulled from
    // this picker entirely, and the gate has to hold either way.
    const { access: hwaAccess } = useHwaAccess();
    const includeHwaVoices = hwaAccess.unlockedCourses.length > 0;

    const catalog = useMemo<MeditationVoiceOption[]>(() => {
        return getAvailableMeditationVoices({
            includeResourcesVoices,
            includeHwaVoices,
        }).map<MeditationVoiceOption>((voice) => ({
            value: voice.value,
            label: voice.label,
            kind: "catalog",
        }));
    }, [includeResourcesVoices, includeHwaVoices]);
    const custom = useMemo<MeditationVoiceOption[]>(() => {
        return customVoices.map<MeditationVoiceOption>((entry) => ({
            value: entry.value as SelectableVoice,
            label: entry.label,
            shortLabel: entry.shortLabel,
            kind: "custom",
            practitionerId: entry.practitionerId ?? undefined,
            ownedBySelf: entry.ownedBySelf,
        }));
    }, [customVoices]);

    // AIDEV-NOTE: Consumers only read defaultVoice inside effects (post-hydration),
    // so the localStorage read here can't cause a hydration mismatch.
    const stored = readVoicePreference(context);
    const customVoiceKey = useMemo(
        () => custom.map((option) => option.value).join("|"),
        [custom],
    );

    // Identity of the INHERITED set only — a practitioner's own clone must not trigger
    // the client-facing switch.
    const inheritedKey = useMemo(
        () =>
            custom
                .filter((option) => !option.ownedBySelf)
                .map((option) => option.value)
                .join("|"),
        [custom],
    );
    const newlyInheritedVoice = useMemo(() => {
        if (!inheritedKey) return null;
        if (inheritedKey === readSeenInheritedKey()) return null;
        return custom.find((option) => !option.ownedBySelf) ?? null;
    }, [custom, inheritedKey]);

    // Stamp AFTER the takeover has been handed to the caller, so it fires exactly once
    // per inherited set rather than on every mount.
    useEffect(() => {
        if (newlyInheritedVoice && inheritedKey) {
            rememberSeenInheritedKey(inheritedKey);
        }
    }, [newlyInheritedVoice, inheritedKey]);

    const resolved = useMemo(() => {
        return resolveMeditationVoiceOptions({
            catalog,
            custom,
            storedVoice: stored,
            context,
            newlyInheritedVoice,
        });
    }, [catalog, context, custom, stored, newlyInheritedVoice]);
    const { options, defaultVoice } = resolved;

    return {
        options,
        defaultVoice,
        customVoiceAvailable: available,
        customVoiceKey,
        requestableVoices: customVoice?.requestable ?? [],
    };
}
